import React from "react";
import {
  Box,
  Typography,
  FormControl,
  FormLabel,
  FormControlLabel,
  Select,
  MenuItem,
  Checkbox,
  TextField,
  FormHelperText,
} from "@mui/material";
import type {
  StatTestType,
  SignificanceLevel,
  DonutStatisticalTestingConfig,
} from "@store/slices/donut-chart-config-slice";

interface StatisticalTestingSectionProps {
  statisticalTesting: DonutStatisticalTestingConfig;
  isDisabled: boolean;
  isAllTimePeriod: boolean;
  rollingAverageEnabled?: boolean;
  onStatTestingChange: (
    updateFn: () => void,
    newValues?: {
      enabled?: boolean;
      significanceLevel?: SignificanceLevel;
      minimumSampleSize?: number;
      testType?: StatTestType;
    },
  ) => void;
  updateStatTestingEnabled: (enabled: boolean) => void;
  updateStatTestType: (testType: StatTestType) => void;
  updateSignificanceLevel: (level: SignificanceLevel) => void;
  updateMinimumSampleSize: (size: number) => void;
}

const StatisticalTestingSection: React.FC<StatisticalTestingSectionProps> = ({
  statisticalTesting,
  isDisabled,
  isAllTimePeriod,
  rollingAverageEnabled = false,
  onStatTestingChange,
  updateStatTestingEnabled,
  updateStatTestType,
  updateSignificanceLevel,
  updateMinimumSampleSize,
}) => {
  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
        Statistical Testing
      </Typography>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
          mt: 2,
        }}
      >
        <FormControlLabel
          control={
            <Checkbox
              disabled={isDisabled || isAllTimePeriod}
              checked={statisticalTesting?.enabled || false}
              onChange={(e) => {
                onStatTestingChange(
                  () => updateStatTestingEnabled(e.target.checked),
                  { enabled: e.target.checked },
                );
              }}
              size="small"
            />
          }
          label="Enable Statistical Testing"
        />

        {isAllTimePeriod && (
          <Typography variant="caption" color="text.secondary">
            Statistical testing requires a specific time period selection.
          </Typography>
        )}

        {statisticalTesting?.enabled && (
          <>
            <FormControl fullWidth size="small">
              <FormLabel className="text-[12px] text-[#262626]">
                Test Type
              </FormLabel>
              <Select
                disabled={isDisabled}
                value={statisticalTesting?.testType || "z-test"}
                onChange={(e) => {
                  onStatTestingChange(
                    () => updateStatTestType(e.target.value as StatTestType),
                    { testType: e.target.value as StatTestType },
                  );
                }}
              >
                <MenuItem value="z-test">Z-Test (Proportions)</MenuItem>
                <MenuItem value="t-test">T-Test (with Overlap)</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth size="small">
              <FormLabel className="text-[12px] text-[#262626]">
                Significance Level
              </FormLabel>
              <Select
                disabled={isDisabled}
                value={statisticalTesting?.significanceLevel || "90%"}
                onChange={(e) => {
                  onStatTestingChange(
                    () =>
                      updateSignificanceLevel(
                        e.target.value as SignificanceLevel,
                      ),
                    {
                      significanceLevel: e.target.value as SignificanceLevel,
                    },
                  );
                }}
              >
                <MenuItem value="90%">90% Confidence</MenuItem>
                <MenuItem value="95%">95% Confidence</MenuItem>
                <MenuItem value="99%">99% Confidence</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth size="small">
              <FormLabel className="text-[12px] text-[#262626] mb-1">
                Minimum Sample Size
              </FormLabel>
              <TextField
                disabled={isDisabled}
                type="text"
                value={String(statisticalTesting?.minimumSampleSize || 30)}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === "" || /^\d+$/.test(value)) {
                    const newSize = value === "" ? 0 : Number(value);
                    onStatTestingChange(
                      () => updateMinimumSampleSize(newSize),
                      { minimumSampleSize: newSize },
                    );
                  }
                }}
                size="small"
                fullWidth
                inputProps={{
                  inputMode: "numeric",
                  pattern: "[0-9]*",
                }}
                sx={{
                  "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button":
                    { display: "none" },
                  "& input[type=number]": {
                    MozAppearance: "textfield",
                  },
                }}
              />
              <FormHelperText>
                Minimum sample size required for statistical testing
              </FormHelperText>
            </FormControl>

            <Box
              sx={{
                p: 2,
                backgroundColor: "#f5f5f5",
                borderRadius: 1,
                border: "1px solid #e0e0e0",
              }}
            >
              <Typography
                variant="caption"
                sx={{ fontWeight: 600, display: "block", mb: 1 }}
              >
                Statistical Markers Legend:
              </Typography>
              <Typography variant="caption" sx={{ display: "block" }}>
                <strong>▲</strong> Significantly higher than{" "}
                {rollingAverageEnabled ? "previous rolling window" : "previous period"}
              </Typography>
              <Typography variant="caption" sx={{ display: "block" }}>
                <strong>▼</strong> Significantly lower than{" "}
                {rollingAverageEnabled ? "previous rolling window" : "previous period"}
              </Typography>
            </Box>
          </>
        )}

        <Typography variant="caption" color="text.secondary">
          Statistical testing helps identify significant differences in your
          data
        </Typography>
      </Box>
    </Box>
  );
};

export default StatisticalTestingSection;
